import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../ui/card";
import { Badge } from "../../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Globe,
  MapPin,
  Database,
  CheckCircle2,
  Filter,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  getCountriesWithCities,
  getCountriesWithCitiesQuery,
  updateCountry,
  addCity,
  updateCity,
  deleteCity,
} from "../../services/countryCityService";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { removeFullSpecialCharacters } from "utils/commonutils";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";

interface City {
  id: string;
  name: string;
  code: string;
  status: "active" | "inactive";
  displayName?: string;
}

interface Country {
  id: string;
  name: string;
  code: string;
  iso2: string;
  iso3: string;
  dialCode?: string;
  countryCode: string;
  cities: City[];
  status: "active" | "inactive";
  displayName?: string;
}

export const CountryCityMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddCityDialogOpen, setIsAddCityDialogOpen] = useState(false);
  const [isEditCountryDialogOpen, setIsEditCountryDialogOpen] = useState(false);
  const [isEditCityDialogOpen, setIsEditCityDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [sortField, setSortField] = useState<"name" | "code" | "cities" | null>(
    null
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);

 

  const { canAdd, canEdit, screenName, icon: ScreenIcon } = useScreenPermission();

  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    countryStatus: "all" as "all" | "active" | "inactive",
    cityStatus: "all" as "all" | "active" | "inactive",
    countryCode: "",
    countryName: "",
    hasNoCities: false,
  });

  const [showFilters, setShowFilters] = useState(false);


  const paginatedCountries = countries.slice(
    (pageNo - 1) * pageSize,
    pageNo * pageSize
  );

  useEffect(() => {
    setPageHeader({
      title: screenName || "Country & City Master",
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);

      const apiData = await getCountriesWithCities({
        searchText: null,
        countryName: null,
        countryCode: null,
        countryIsActive: null,
        cityIsActive: null,
        countriesWithNoCities: false,
      });

      const mappedCountries: Country[] = apiData.map((c) => ({
        id: String(c.countryId),
        name: c.countryName,
        iso2: c.iso2,
        iso3: c.iso3,
        code: c.iso2,
        dialCode: c.dialCode ?? "",
        countryCode: `${c.iso2} / ${c.iso3}`,
        status: c.isActive ? "active" : "inactive",
        cities: c.cities.map((city) => ({
          id: String(city.cityId),
          name: city.cityName,
          code: city.postalCode ?? "",
          status: city.isActive ? "active" : "inactive",
        })),
      }));

      setCountries(mappedCountries);
    } catch (error) {
      toast.error("Failed to load countries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = {
          searchText: searchTerm || null,
          countryName: filters.countryName || null,
          countryCode: filters.countryCode || null,
          countryIsActive:
            filters.countryStatus === "all"
              ? null
              : filters.countryStatus === "active",
          cityIsActive:
            filters.cityStatus === "all"
              ? null
              : filters.cityStatus === "active",
          countriesWithNoCities: filters.hasNoCities,
        };

        const data = await getCountriesWithCitiesQuery(payload);

        const mapped: Country[] = data.map((c) => ({
          id: String(c.countryId),
          name: c.countryName,
          iso2: c.iso2,
          iso3: c.iso3,
          code: c.iso2,
          dialCode: c.dialCode ?? "",
          countryCode: `${c.iso2} / ${c.iso3}`,
          status: c.isActive ? "active" : "inactive",
          cities: c.cities.map((city) => ({
            id: String(city.cityId),
            name: city.cityName,
            code: city.postalCode ?? "",
            status: city.isActive ? "active" : "inactive",
          })),
        }));

        setCountries(mapped);
      } catch {
        toast.error("Failed to load countries");
      }
    };

    fetchData();
  }, [filters, searchTerm]);

  const [cityFormData, setCityFormData] = useState({
    cityName: "",
    postalCode: "",
    status: "active" as "active" | "inactive",
  });

  const [cityErrors, setCityErrors] = useState<{
    cityName?: string;
    postalCode?: string;
  }>({});

  const validateCityForm = () => {
    const errors: typeof cityErrors = {};

    const cityCodeRegex = /^[A-Za-z]{1,5}$/;

    if (!cityFormData.cityName.trim()) {
      errors.cityName = "City name is required";
    } else if (cityFormData.cityName.trim().length > 20) {
      errors.cityName = "City name cannot exceed 20 characters";
    }

    if (!cityFormData.postalCode.trim()) {
      errors.postalCode = "City code is required";
    } else if (!cityCodeRegex.test(cityFormData.postalCode.trim())) {
      errors.postalCode =
        "City code must contain only alphabets and be a maximum of 5 characters";
    }

    setCityErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const [editCountryFormData, setEditCountryFormData] = useState({
    selectedCountry: "",
    name: "",
    displayName: "",
    code: "",
    dialCode: "",
    countryCode: "",
    status: "active" as "active" | "inactive",
  });

  const [editCityFormData, setEditCityFormData] = useState({
    name: "",
    code: "",
    status: "active" as "active" | "inactive",
  });

  const handleSort = (field: "name" | "code" | "cities") => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortField(null);
        setSortOrder(null);
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPageNo(1);
  };

  const handleClearFilters = () => {
    setFilters({
      countryStatus: "all",
      cityStatus: "all",
      countryCode: "",
      countryName: "",
      hasNoCities: false,
    });
    setPageNo(1);
    toast.success("Filters cleared");
  };

  const handleAddCity = async () => {
    if (!selectedCountry) return;

    const isValid = validateCityForm();
    if (!isValid) return;

    try {
      await addCity({
        cityName: cityFormData.cityName,
        cityCode: cityFormData.postalCode,
        postalCode: cityFormData.postalCode,
        countryId: Number(selectedCountry.id),
        isActive: cityFormData.status === "active",
      });

      toast.success("City added successfully");

      await fetchCountries();

      setIsAddCityDialogOpen(false);
      setSelectedCountry(null);
      setCityFormData({
        cityName: "",
        postalCode: "",
        status: "active",
      });
      setCityErrors({});
    } catch {
      toast.error("Failed to add city");
    }
  };

  const handleDeleteCity = async (cityId: string) => {
    try {
      await deleteCity(Number(cityId));

      toast.success("City deleted successfully");

      await fetchCountries();
    } catch (error) {
      toast.error("Failed to delete city");
    }
  };

  const handleEditCountry = (country: Country) => {
    setEditingCountry(country);
    setEditCountryFormData({
      selectedCountry: country.name,
      name: country.name,
      displayName: country.displayName || "",
      code: country.iso2,
      dialCode: country.dialCode || "",
      countryCode: `${country.iso2} / ${country.iso3}`,
      status: country.status,
    });
    setIsEditCountryDialogOpen(true);
  };

  const handleUpdateCountry = async () => {
    if (!editingCountry) return;

    const dialCodeRegex = /^\+\d{1,4}$/;
    if (!dialCodeRegex.test(editCountryFormData.dialCode)) {
      toast.error("Dial code is not valid");
      return;
    }

    try {
      await updateCountry({
        countryId: Number(editingCountry.id),
        countryName: editCountryFormData.name,
        iso2: editingCountry.iso2,
        iso3: editingCountry.iso3,
        dialCode: editCountryFormData.dialCode,
        isActive: editCountryFormData.status === "active",
        updatedBy: 0,
      });

      toast.success("Country updated successfully");

      await fetchCountries();

      setIsEditCountryDialogOpen(false);
      setEditingCountry(null);
    } catch (error: unknown) {
      if (typeof error === "object" && error !== null && "response" in error) {
        const err = error as {
          response?: {
            data?: {
              message?: string;
            };
          };
        };

        const message = err.response?.data?.message?.toLowerCase() || "";

        if (message.includes("dial")) {
          toast.error("Dial code is not correct");
        } else if (message.includes("country code")) {
          toast.error("Country code is invalid");
        } else {
          toast.error("Failed to update country");
        }
      } else {
        toast.error("Failed to update country");
      }
    }
  };

  const handleEditCity = (country: Country, city: City) => {
    setSelectedCountry(country);
    setEditingCity(city);
    setEditCityFormData({
      name: city.name,
      code: city.code,
      status: city.status,
    });
    setIsEditCityDialogOpen(true);
  };

  const handleUpdateCity = async () => {
    if (!editingCity || !selectedCountry) return;

    const cityCodeRegex = /^[A-Za-z]{1,5}$/;

    if (!editCityFormData.name.trim()) {
      toast.error("City name is required");
      return;
    }

    if (editCityFormData.name.trim().length > 20) {
      toast.error("City name cannot exceed 20 characters");
      return;
    }

    if (!cityCodeRegex.test(editCityFormData.code.trim())) {
      toast.error(
        "City code must contain only alphabets and be a maximum of 5 characters"
      );
      return;
    }

    try {
      await updateCity({
        cityId: Number(editingCity.id),
        cityName: editCityFormData.name,
        postalCode: editCityFormData.code,
        cityCode: editCityFormData.code,
        countryId: Number(selectedCountry.id),
        isActive: editCityFormData.status === "active",
      });

      toast.success("City updated successfully");

      await fetchCountries();

      setIsEditCityDialogOpen(false);
      setEditingCity(null);
      setSelectedCountry(null);
      setEditCityFormData({
        name: "",
        code: "",
        status: "active",
      });
    } catch (error) {
      toast.error("Failed to update city");
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      active: "bg-green-100 text-green-700 border-green-300",
      inactive: "bg-gray-100 text-gray-700 border-gray-300",
    };

    return (
      <Badge className={variants[status] || variants.active}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="border-green-200 bg-green-50/50" style={{ marginTop: "0px" }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Active Countries</p>
                  <p className="text-2xl font-bold text-green-900">
                    {countries.filter((c) => c.status === "active").length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-emerald-200 bg-emerald-50/50" style={{ marginTop: "0px" }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600">Active Cities</p>
                  <p className="text-2xl font-bold text-emerald-900">
                    {countries.reduce(
                      (sum, country) =>
                        sum +
                        country.cities.filter((city) => city.status === "active")
                          .length,
                      0
                    )}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2" style={{ marginTop: "10px" }}>
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

            <Input
              placeholder="Search countries or cities..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="h-10 pl-9 pr-9 shadow-sm"
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => handleSearchChange("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            style={showFilters ? { background: "#5B9BD5" } : {}}
            className={`shrink-0 ${
              showFilters
                ? "text-white"
                : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
            }`}
            title="Toggle Advanced Filters"
          >
            <Filter className="w-5 h-5" />
          </Button>
        </div>


        {showFilters && (
          <Card
            className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
            style={{ gap: "0px" }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2 text-lg"
                  >
                    <Filter className="w-5 h-5" />
                    Advanced Filters
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Filter countries and cities by multiple criteria
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="filter-country-name" className="text-sm">
                    Country Name
                  </Label>
                  <Input
                    id="filter-country-name"
                    placeholder="Search by country name..."
                    value={filters.countryName}
                    onChange={(e) =>
                      setFilters({ ...filters, countryName: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-country-code" className="text-sm">
                    Country Code
                  </Label>
                  <Input
                    id="filter-country-code"
                    placeholder="e.g., IQ, SA, IR"
                    value={filters.countryCode}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        countryCode: e.target.value.toUpperCase(),
                      })
                    }
                    className="font-mono h-9 text-sm"
                    maxLength={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-country-status" className="text-sm">
                    Country Status
                  </Label>

                  <Select
                    value={filters.countryStatus}
                    onValueChange={(value: "all" | "active" | "inactive") =>
                      setFilters({ ...filters, countryStatus: value })
                    }
                  >
                    <SelectTrigger
                      id="filter-country-status"
                      className="h-9 text-sm"
                    >
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-city-status" className="text-sm">
                    City Status
                  </Label>

                  <Select
                    value={filters.cityStatus}
                    onValueChange={(value: "all" | "active" | "inactive") =>
                      setFilters({ ...filters, cityStatus: value })
                    }
                  >
                    <SelectTrigger
                      id="filter-city-status"
                      className="h-9 text-sm"
                    >
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Cities</SelectItem>
                      <SelectItem value="inactive">Inactive Cities</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Special Filters</Label>
                  <div className="flex items-center space-x-2 h-9 px-3 border rounded-md bg-white">
                    <input
                      type="checkbox"
                      id="filter-no-cities"
                      checked={filters.hasNoCities}
                      onChange={(e) =>
                        setFilters({ ...filters, hasNoCities: e.target.checked })
                      }
                      className="w-4 h-4 text-[#5B9BD5] border-gray-300 rounded focus:ring-[#5B9BD5]"
                    />
                    <Label
                      htmlFor="filter-no-cities"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Countries with no cities
                    </Label>
                  </div>
                </div>
              </div>

              {(filters.countryName ||
                filters.countryCode ||
                filters.countryStatus !== "all" ||
                filters.cityStatus !== "all" ||
                filters.hasNoCities) && (
                  <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                        Active Filters:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {filters.countryName && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Country: {filters.countryName}
                          </Badge>
                        )}
                        {filters.countryCode && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Code: {filters.countryCode}
                          </Badge>
                        )}
                        {filters.countryStatus !== "all" && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Country: {filters.countryStatus}
                          </Badge>
                        )}
                        {filters.cityStatus !== "all" && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            City: {filters.cityStatus}
                          </Badge>
                        )}
                        {filters.hasNoCities && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            No Cities Only
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearFilters}
                      className="border-red-300 text-red-600 hover:bg-red-50 flex-shrink-0"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  </div>
                )}
            </CardContent>
          </Card>
        )}

        <Card className="bg-white border-[#5B9BD5]/30 hidden lg:block" style={{ marginTop: "10px" }}>

          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-sky-50 to-blue-50">
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-6 py-4 cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Country
                        {sortField === "name" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-6 py-4 cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("code")}
                    >
                      <div className="flex items-center gap-1">
                        Code
                        {sortField === "code" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead style={{ color: "#5B9BD5" }} className="px-6 py-4">
                      Dial Code
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-6 py-4 cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("cities")}
                    >
                      <div className="flex items-center gap-1">
                        Cities
                        {sortField === "cities" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead style={{ color: "#5B9BD5" }} className="px-6 py-4">
                      Status
                    </TableHead>
                    {canEdit && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-6 py-4 text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {countries.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 px-6 text-gray-500"
                      >
                        No countries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedCountries.map((country) => (
                      <TableRow key={country.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <Globe className="w-5 h-5 text-[#5B9BD5]" />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-900 font-medium">
                                {country.displayName || country.name}
                              </span>
                              {country.displayName && (
                                <span className="text-xs text-gray-500">
                                  ({country.name})
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-6 py-4">
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] font-medium"
                          >
                            {country.countryCode || country.code}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-6 py-4">
                          <span className="text-gray-700">
                            {country.dialCode || "-"}
                          </span>
                        </TableCell>
                        <TableCell className="px-6 py-4">
                          <div className="space-y-2">
                            {country.cities.length === 0 ? (
                              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <p className="text-sm text-gray-500 italic">
                                  No cities added yet
                                </p>
                              </div>
                            ) : (
                              country.cities.map((city) => (
                                <div
                                  key={city.id}
                                  className="flex items-center gap-4 p-3 bg-gradient-to-r from-sky-50 to-blue-50 rounded-lg border border-sky-200 hover:border-sky-300 transition-colors"
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                                      <MapPin className="w-4 h-4 text-[#5B9BD5]" />
                                    </div>

                                    <div className="flex flex-col min-w-0">
                                      <span className="text-sm font-medium text-gray-900 truncate">
                                        {city.displayName || city.name}
                                      </span>
                                      {city.displayName && (
                                        <span className="text-xs text-gray-500 truncate">
                                          ({city.name})
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div className="w-20 flex items-center justify-center">
                                    <Badge
                                      variant="outline"
                                      className="text-xs border-[#5B9BD5]/30 text-[#5B9BD5] font-medium"
                                    >
                                      {city.code}
                                    </Badge>
                                  </div>

                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {getStatusBadge(city.status)}

                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        handleEditCity(country, city)
                                      }
                                      className="text-[#5B9BD5] hover:bg-white h-8 w-8 p-0"
                                      title="Edit city"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </Button>

                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteCity(city.id)}
                                      className="text-red-600 hover:bg-white h-8 w-8 p-0"
                                      title="Delete city"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                            {canAdd && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedCountry(country);
                                  setIsAddCityDialogOpen(true);
                                }}
                                className="w-full text-[#5B9BD5] hover:bg-sky-100 border border-dashed border-sky-300 py-2"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Add City to {country.displayName || country.name}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-6 py-4">
                          {getStatusBadge(country.status)}
                        </TableCell>
                        {canEdit && (
                          <TableCell className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditCountry(country)}
                                className="text-[#5B9BD5] hover:bg-sky-50"
                                title="Edit country"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          
          {/* Mobile Pagination */}
          <Pagination
            totalItems={countries.length}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={setPageNo}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="countries"
          />
        </Card>

        {/* Mobile Card View */}
        <Card className="bg-white border-[#5B9BD5]/30 lg:hidden">
          <CardHeader>
            <CardTitle style={{ color: "#5B9BD5" }}>
              Countries & Cities
            </CardTitle>
            <CardDescription>
              {countries.length} countries found
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {countries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No countries found
              </div>
            ) : (
              paginatedCountries.map((country) => (
                <Card
                  key={country.id}
                  className="border-[#5B9BD5]/30 bg-gradient-to-br from-white to-sky-50/30"
                >
                  <CardContent className="p-4 space-y-3">
                    {/* Country Header */}
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {country.displayName || country.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {country.countryCode}
                        </p>
                      </div>
                      {getStatusBadge(country.status)}
                    </div>

                    {/* Dial Code */}
                    <div className="text-sm text-gray-700">
                      Dial Code: {country.dialCode || "-"}
                    </div>

                    {/* Cities */}
                    <div className="space-y-2">
                      {country.cities.length === 0 ? (
                        <div className="text-sm text-gray-400 italic">
                          No cities added
                        </div>
                      ) : (
                        country.cities.map((city) => (
                          <div
                            key={city.id}
                            className="flex justify-between items-center bg-sky-50 p-2 rounded-md"
                          >
                            <div>
                              <div className="text-sm font-medium">
                                {city.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {city.code}
                              </div>
                            </div>
                            {getStatusBadge(city.status)}
                          </div>
                        ))
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleEditCountry(country)}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setSelectedCountry(country);
                          setIsAddCityDialogOpen(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add City
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </CardContent>
          <Pagination
          totalItems={countries.length}
          itemsPerPage={pageSize}
          currentPage={pageNo}
          onPageChange={setPageNo}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPageNo(1);
          }}
          label="countries"
        />
        </Card>

        

        <Dialog
          open={isEditCountryDialogOpen}
          onOpenChange={setIsEditCountryDialogOpen}
        >
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>Edit Country</DialogTitle>
              <DialogDescription>
                Update the country details, dial code, and country code
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Country Name"
                  required
                  id="editCountryName"
                  placeholder="e.g., Baghdad"
                  value={editCountryFormData.name}
                  onChange={(e) => {
                    const value = removeFullSpecialCharacters(e.target.value)
                    setEditCountryFormData({
                      ...editCountryFormData,
                      name: value,
                    })
                  }}
                />
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Dial Code"
                  required
                  id="editDialCode"
                  placeholder="e.g., +964"
                  value={editCountryFormData.dialCode}
                  onChange={(e) =>
                    setEditCountryFormData({
                      ...editCountryFormData,
                      dialCode: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-gray-500">
                  Country calling code (auto-filled when country is selected)
                </p>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Country Code"
                  required
                  id="editCountryCode"
                  disabled
                  placeholder="e.g., IQ / IRQ"
                  value={editCountryFormData.countryCode}
                  onChange={(e) =>
                    setEditCountryFormData({
                      ...editCountryFormData,
                      countryCode: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-gray-500">
                  ISO2 / ISO3 country code (auto-filled when country is selected)
                </p>
              </div>

              {/* <div className="space-y-2">
                <Label htmlFor="editCountryStatus">
                  Status <span className="text-red-500">*</span>
                </Label>

                <Select
                  value={editCountryFormData.status}
                  onValueChange={(value: "active" | "inactive") =>
                    setEditCountryFormData({
                      ...editCountryFormData,
                      status: value,
                    })
                  }
                >
                  <SelectTrigger id="editCountryStatus">
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              {editCountryFormData.displayName && (
                <Card className="p-3 bg-blue-50/50 border-blue-200">
                  <div className="flex items-start gap-2">
                    <Database className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="text-xs text-blue-800">
                      <strong>Preview:</strong> This country will be displayed as
                      "{editCountryFormData.displayName}" (Original:{" "}
                      {editCountryFormData.name})
                    </div>
                  </div>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditCountryDialogOpen(false);
                  setEditingCountry(null);
                  setEditCountryFormData({
                    selectedCountry: "",
                    name: "",
                    displayName: "",
                    code: "",
                    dialCode: "",
                    countryCode: "",
                    status: "active",
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateCountry}
                disabled={
                  !editCountryFormData.name ||
                  !editCountryFormData.dialCode ||
                  !editCountryFormData.countryCode
                }
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                Update Country
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isEditCityDialogOpen}
          onOpenChange={setIsEditCityDialogOpen}
        >
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>Edit City</DialogTitle>
              <DialogDescription>
                Update the city code and status
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <InputFieldset
                  label="City Name"
                  required
                  value={editCityFormData.name}
                  maxLength={20}
                  onChange={(e) => {
                    const value = removeFullSpecialCharacters(e.target.value).slice(0, 20);
                    setEditCityFormData({
                      ...editCityFormData,
                      name: value,
                    })
                  }}
                />
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="City Code"
                  required
                  id="editpostalCode"
                  placeholder="e.g., KRB"
                  value={editCityFormData.code}
                  onChange={(e) =>
                    setEditCityFormData({
                      ...editCityFormData,
                      code: e.target.value
                        .replace(/[^a-zA-Z]/g, "")
                        .toUpperCase(),
                    })
                  }
                  maxLength={5}
                />
                <p className="text-xs text-gray-500">
                  Internal reference code (2-5 characters)
                </p>
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status" required>
                  <Select
                    value={editCityFormData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setEditCityFormData({
                        ...editCityFormData,
                        status: value,
                      })
                    }
                  >
                    <SelectTriggerUI id="editCityStatus">
                      <SelectValue />
                    </SelectTriggerUI>

                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditCityDialogOpen(false);
                  setEditingCity(null);
                  setSelectedCountry(null);
                  setEditCityFormData({ name: "", code: "", status: "active" });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateCity}
                disabled={!editCityFormData.code}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                Update City
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isAddCityDialogOpen} onOpenChange={setIsAddCityDialogOpen}>
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Add City to{" "}
                {selectedCountry?.displayName || selectedCountry?.name}
              </DialogTitle>
              <DialogDescription>
                Enter the city name and code to add to this country
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <InputFieldset
                  label="City Name"
                  required
                  id="cityName"
                  placeholder="e.g., Karbala"
                  value={cityFormData.cityName}
                  maxLength={20}
                  onChange={(e) => {
                    const value = removeFullSpecialCharacters(e.target.value).slice(0, 20);
                    setCityFormData({
                      ...cityFormData,
                      cityName: value,
                    });
                    setCityErrors((prev) => ({ ...prev, cityName: undefined }));
                  }}
                />

                {cityErrors.cityName && (
                  <p className="text-xs text-red-600">{cityErrors.cityName}</p>
                )}

                <p className="text-xs text-gray-500">
                  Enter the name of the city
                </p>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="City Code"
                  required
                  id="postalCode"
                  placeholder="e.g., KRB"
                  value={cityFormData.postalCode}
                  onChange={(e) => {
                    setCityFormData({
                      ...cityFormData,
                      postalCode: e.target.value
                        .replace(/[^a-zA-Z]/g, "")
                        .toUpperCase(),
                    });
                    setCityErrors((prev) => ({ ...prev, postalCode: undefined }));
                  }}
                  maxLength={5}
                />

                {cityErrors.postalCode && (
                  <p className="text-xs text-red-600">{cityErrors.postalCode}</p>
                )}

                <p className="text-xs text-gray-500">
                  Internal reference code (2-5 characters)
                </p>
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={cityFormData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setCityFormData({
                        ...cityFormData,
                        status: value,
                      })
                    }
                  >
                    <SelectTriggerUI id="cityStatus">
                      <SelectValue />
                    </SelectTriggerUI>

                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>

              {cityFormData.cityName && (
                <Card className="p-3 bg-blue-50/50 border-blue-200">
                  <div className="flex items-start gap-2">
                    <Database className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="text-xs text-blue-800">
                      <strong>Preview:</strong> This city will be saved as "
                      {cityFormData.cityName}"
                    </div>
                  </div>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddCityDialogOpen(false);
                  setSelectedCountry(null);
                  setCityFormData({
                    cityName: "",
                    postalCode: "",
                    status: "active",
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddCity}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                Add City
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
